/**
 * Keeps the header cart count in sync.
 *
 * The storefront uses a custom header, so Shopify's <cart-icon> element is
 * never rendered and nothing was updating the count after a cart mutation.
 * This listens to the same event <cart-icon> does, so it stays correct
 * wherever the cart changes: the cart page, quick add, or the product form.
 */
(() => {
  const SESSION_KEY = 'cart-count';
  const counters = () => document.querySelectorAll('[data-cart-count]');

  /**
   * @param {number} count
   */
  const render = (count) => {
    if (!Number.isFinite(count) || count < 0) return;

    for (const element of counters()) {
      if (element.textContent === String(count)) continue;
      element.textContent = String(count);
      element.setAttribute('aria-label', `${count} ${count === 1 ? 'item' : 'items'}`);
    }

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch (_) {
      // private mode; the count is still correct for this page
    }
  };

  /** Last resort when an event arrives without a resolved cart. */
  const refetch = async () => {
    try {
      const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart.js` : '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      render(cart.item_count);
    } catch (_) {
      // leave the current number rather than showing a wrong one
    }
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    const promise = /** @type {{ promise?: Promise<any>, detail?: any }} */ (event).promise;

    if (!promise) {
      refetch();
      return;
    }

    promise
      .then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount;
        if (count == null) {
          refetch();
          return;
        }
        render(count);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        // A failed mutation should not leave a stale number on screen.
        refetch();
      });
  });

  /**
   * Restoring from the back/forward cache replays stale markup, and Shopify's
   * full-page cache can serve a cached header. Reconcile against the count we
   * recorded when the cart last changed.
   */
  const reconcile = () => {
    let stored;
    try {
      stored = sessionStorage.getItem(SESSION_KEY);
    } catch (_) {
      return;
    }
    if (!stored) return;

    try {
      const { value, timestamp } = JSON.parse(stored);
      const [first] = counters();
      if (!first || first.textContent === value) return;
      // Only trust a recent record; otherwise the server-rendered value wins.
      if (Date.now() - timestamp > 10000) return;
      render(parseInt(value, 10));
    } catch (_) {
      // no-op
    }
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcile();
  });

  reconcile();
})();
